/**
 * Identity of the last signed-in user, remembered in this browser so the app can open its local
 * database when the API is unreachable (docs/07 §7: the app must work without the network).
 * A tiny external store: React reads it with `useSyncExternalStore`.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

const CACHE_KEY = 'flow.session';
const listeners = new Set<() => void>();

function read(): SessionUser | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

let current: SessionUser | null = read();

export function getCachedUser(): SessionUser | null {
  return current;
}

export function setCachedUser(user: SessionUser | null): void {
  if (current?.id === user?.id && current?.email === user?.email) return;
  current = user;
  try {
    if (user) localStorage.setItem(CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    // Storage may be unavailable (private mode); the in-memory value still serves this page load.
  }
  for (const listener of listeners) listener();
}

export function subscribeCachedUser(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
